//  Angular imports
import { Injectable, signal } from '@angular/core';
//  Application imports
import { FortiCloudBaseProvisioningService } from './base-provisioning.service';
import { Endpoints } from './endpoints';
import { RAccount } from 'app/records/account.record';
//  Third party imports
import { Observable, of } from 'rxjs';
import { RAccountInformationDetails } from 'app/records/account-information-details-records';
import { ROrganization } from 'app/records/organization-record';

@Injectable({
    providedIn: 'root'
})
export class UserService extends FortiCloudBaseProvisioningService {

    // Session state. Written only through the methods below; components read the
    // read-only signals directly in their templates and computeds.
    private readonly _accountSelected = signal<RAccount>(null);
    private readonly _organizations = signal<Array<ROrganization>>(null);
    private readonly _isAuthenticated = signal<boolean>(false);
    private readonly _userId = signal<string>(null);
    private readonly _accountId = signal<string>(null);

    public readonly accountSelected = this._accountSelected.asReadonly();
    public readonly organizations = this._organizations.asReadonly();
    public readonly isAuthenticated = this._isAuthenticated.asReadonly();
    public readonly userId = this._userId.asReadonly();
    public readonly accountId = this._accountId.asReadonly();

    public getAccount(accountId: number, userId: number): Observable<RAccount> {
        // check if account id is 0 and don't even call the API
        if (accountId === 0) { return of(null); }
        return this.apiGet<RAccount>(
            `${Endpoints.USER}/${accountId}/${userId ? userId : ''}`,
            RAccount
        ) as Observable<RAccount>;
    }

    public getAccounts(observeResponse?: boolean): Observable<RAccountInformationDetails> {
        return this.apiGet<RAccountInformationDetails>(
            Endpoints.USER,
            RAccountInformationDetails,
            observeResponse
        ) as Observable<RAccountInformationDetails>;
    }

    public getOrgPermissions(nodeId: number): Observable<RAccount> {
        // check if account id is 0 and don't even call the API
        if (nodeId === 0) { return of(null); }
        return this.apiGet<RAccount>(
            `${Endpoints.USER}/org/${nodeId}`,
            RAccount
        ) as Observable<RAccount>;
    }

    public selectAccount(account: RAccount): void {
        this._accountSelected.set(account);
    }

    public setAuthenticated(): void {
        this._isAuthenticated.set(true);
    }

    public setOrganizations(items: Array<ROrganization>) {
        if (items.length > 0) {
            this._organizations.set(items);
        }
    }

    public setGlobalUserId(userId: string): void {
        if (userId) {
            this._userId.set(userId);
        }
    }

    public setGlobalIAMAccount(accountId: string): void {
        if (accountId) {
            this._accountId.set(accountId);
        }
    }

    public clearAuthenticated(): void {
        this._isAuthenticated.set(false);
    }
}
